/**
 * Parse Movie Magic Budget CSV exports (client-side).
 * Amount values are already in millions (e.g. 1.24 = $1.24M).
 * Currency comes from a Currency column, amount symbols ($/£/€), or header hints.
 */

const AMOUNT_ALIASES = [
  'amount',
  'total',
  'cost',
  'budget',
  'budget amount',
  'line amount',
  'amount (millions)',
  'amount millions',
  'millions',
];
const CURRENCY_ALIASES = ['currency', 'curr', 'ccy'];
const CATEGORY_ALIASES = ['category', 'acct category', 'account category'];
const DESCRIPTION_ALIASES = ['description', 'desc', 'detail', 'line item', 'account name'];
const ACCOUNT_ALIASES = ['account', 'acct', 'account #', 'account number', 'code'];

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Split a single CSV line respecting double-quoted fields. */
function splitCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

function findColIndex(headers, aliases) {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((h) => aliases.includes(h));
}

/**
 * Normalize currency codes / symbols to ISO-ish codes.
 * @returns {'USD'|'GBP'|'EUR'|string}
 */
export function normalizeCurrency(raw) {
  const c = String(raw || '').trim().toUpperCase();
  if (!c) return '';
  if (c === '$' || c === 'US$' || c === 'USD' || c === 'DOLLAR' || c === 'DOLLARS' || c === 'US DOLLAR') {
    return 'USD';
  }
  if (c === '£' || c === 'GBP' || c === 'POUND' || c === 'POUNDS' || c === 'STERLING' || c === 'UKP') {
    return 'GBP';
  }
  if (c === '€' || c === 'EUR' || c === 'EURO' || c === 'EUROS') {
    return 'EUR';
  }
  return c;
}

function currencyFromAmountRaw(raw) {
  const s = String(raw || '');
  if (s.includes('$')) return 'USD';
  if (s.includes('£')) return 'GBP';
  if (s.includes('€')) return 'EUR';
  return '';
}

function currencyFromHeaders(headers) {
  const joined = headers.map(normalizeHeader).join(' | ');
  if (/\b(usd|us\$|dollar)\b/.test(joined) || /\$/.test(joined)) return 'USD';
  if (/\b(gbp|pound|sterling)\b/.test(joined) || /£/.test(joined)) return 'GBP';
  if (/\b(eur|euro)\b/.test(joined) || /€/.test(joined)) return 'EUR';
  return '';
}

function parseAmount(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  const clean = text.replace(/[^\d.,\-]/g, '');
  if (!clean) return null;
  // European 1.234.567,89 vs US 1,234,567.89
  let normalized = clean;
  if (clean.includes(',') && clean.includes('.')) {
    if (clean.lastIndexOf(',') > clean.lastIndexOf('.')) {
      normalized = clean.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = clean.replace(/,/g, '');
    }
  } else if (clean.includes(',')) {
    const parts = clean.split(',');
    normalized = parts.length === 2 && parts[1].length <= 2
      ? clean.replace(',', '.')
      : clean.replace(/,/g, '');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

export function currencySymbol(code) {
  const c = normalizeCurrency(code);
  if (c === 'EUR') return '€';
  if (c === 'USD') return '$';
  if (c === 'GBP') return '£';
  return '';
}

/**
 * Format a total that is already in millions (e.g. 1.24 → $1.24M).
 * @param {number} totalMillions
 * @param {string} [currency]
 */
export function formatBudgetMillions(totalMillions, currency = '') {
  if (!Number.isFinite(totalMillions)) return '-';
  const value = Number(Number(totalMillions).toFixed(6));
  const abs = Math.abs(value);
  let digits;
  if (abs >= 100) digits = 0;
  else if (abs >= 10) digits = 1;
  else digits = 2;
  const formatted = value
    .toFixed(digits)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');
  const symbol = currencySymbol(currency);
  if (symbol) return `${symbol}${formatted}M`;
  const code = normalizeCurrency(currency);
  return code ? `${formatted}M ${code}` : `${formatted}M`;
}

/**
 * @param {string} text Raw CSV file contents
 * @returns {{
 *   total: number,
 *   currency: string,
 *   lineCount: number,
 *   rows: Array<{ account: string, category: string, description: string, amount: number, currency: string }>,
 *   warnings: string[],
 * }}
 */
export function parseBudgetCsv(text) {
  const warnings = [];
  const raw = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!raw) {
    throw new Error('The budget file is empty.');
  }

  const lines = raw.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('The budget CSV must include a header row and at least one line.');
  }

  const headers = splitCsvLine(lines[0]);
  const amountIdx = findColIndex(headers, AMOUNT_ALIASES);
  if (amountIdx < 0) {
    throw new Error('Could not find an Amount column in this budget CSV.');
  }

  const currencyIdx = findColIndex(headers, CURRENCY_ALIASES);
  const categoryIdx = findColIndex(headers, CATEGORY_ALIASES);
  const descriptionIdx = findColIndex(headers, DESCRIPTION_ALIASES);
  const accountIdx = findColIndex(headers, ACCOUNT_ALIASES);
  const headerCurrency = currencyFromHeaders(headers);

  const rows = [];
  let total = 0;
  const currencyCounts = new Map();

  for (let i = 1; i < lines.length; i += 1) {
    const fields = splitCsvLine(lines[i]);
    if (!fields.some((f) => f)) continue;

    const amountRaw = fields[amountIdx];
    const amount = parseAmount(amountRaw);
    if (amount == null) {
      warnings.push(`Row ${i + 1}: skipped (invalid amount).`);
      continue;
    }

    const fromCol = currencyIdx >= 0 ? normalizeCurrency(fields[currencyIdx]) : '';
    const fromAmount = normalizeCurrency(currencyFromAmountRaw(amountRaw));
    const currency = fromCol || fromAmount || headerCurrency;
    if (currency) {
      currencyCounts.set(currency, (currencyCounts.get(currency) || 0) + 1);
    }

    total += amount;
    rows.push({
      account: accountIdx >= 0 ? (fields[accountIdx] || '').trim() : '',
      category: categoryIdx >= 0 ? (fields[categoryIdx] || '').trim() : '',
      description: descriptionIdx >= 0 ? (fields[descriptionIdx] || '').trim() : '',
      amount,
      currency,
    });
  }

  if (!rows.length) {
    throw new Error('No budget line amounts found in this CSV.');
  }

  let currency = headerCurrency;
  if (currencyCounts.size === 1) {
    currency = [...currencyCounts.keys()][0];
  } else if (currencyCounts.size > 1) {
    warnings.push('Multiple currencies found - total sums all Amount values as-is.');
    currency = [...currencyCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  } else if (!currency) {
    warnings.push('No currency found - add a Currency column (USD, GBP, EUR) or $ / £ / € in Amount.');
  }

  return {
    total,
    currency,
    lineCount: rows.length,
    rows,
    warnings,
  };
}
